/**
 * Follow-up reminders — derive overdue / due-soon contacts from
 * `followUpDate`.
 *
 * Pure selectors over a Person[] snapshot, computed on demand so they
 * never drift out of sync with the store. Powers the Dashboard
 * "Follow-ups" widget, the CRM badge, and the outreach-monitor agent's
 * run narration (so it reads as real work rather than a canned string).
 */

import type { Person } from '../db/types';

const DAY_MS = 86_400_000;

export type ReminderUrgency = 'overdue' | 'today' | 'soon';

export interface FollowUpReminder {
  person: Person;
  /** ISO date (YYYY-MM-DD) the follow-up is due. */
  dueDate: string;
  /** Negative = overdue, 0 = today, positive = days out. */
  daysUntil: number;
  urgency: ReminderUrgency;
  /** The contact's nextStep, if set. */
  nextStep?: string;
}

/** Local midnight today, for day-granularity comparisons. */
function startOfToday(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Whole-day delta between a due date and today (negative = past). */
function daysUntil(iso: string): number | undefined {
  const due = Date.parse(iso);
  if (Number.isNaN(due)) return undefined;
  const dueMidnight = new Date(due);
  dueMidnight.setHours(0, 0, 0, 0);
  return Math.round((dueMidnight.getTime() - startOfToday()) / DAY_MS);
}

/**
 * Reminders within `withinDays` of today (default 7), plus everything
 * already overdue. Sorted most-urgent-first (overdue, then soonest).
 * Closed-lost prospects and churned customers are excluded — there's no
 * point reminding about a dead relationship.
 */
export function getFollowUpReminders(
  people: Person[],
  withinDays = 7,
): FollowUpReminder[] {
  const out: FollowUpReminder[] = [];
  for (const person of people) {
    if (!person.followUpDate) continue;
    if (person.stage === 'closed-lost' || person.status === 'churned') continue;
    const d = daysUntil(person.followUpDate);
    if (d === undefined) continue;
    if (d > withinDays) continue;
    const urgency: ReminderUrgency = d < 0 ? 'overdue' : d === 0 ? 'today' : 'soon';
    out.push({
      person,
      dueDate: person.followUpDate,
      daysUntil: d,
      urgency,
      nextStep: person.nextStep,
    });
  }
  return out.sort((a, b) => a.daysUntil - b.daysUntil);
}

/** Just the overdue subset. */
export function getOverdueFollowUps(people: Person[]): FollowUpReminder[] {
  return getFollowUpReminders(people, 3650).filter((r) => r.urgency === 'overdue');
}

/** Counts for badges: { overdue, today, soon, total }. */
export function followUpCounts(people: Person[]): {
  overdue: number;
  today: number;
  soon: number;
  total: number;
} {
  const reminders = getFollowUpReminders(people);
  const overdue = reminders.filter((r) => r.urgency === 'overdue').length;
  const today = reminders.filter((r) => r.urgency === 'today').length;
  const soon = reminders.filter((r) => r.urgency === 'soon').length;
  return { overdue, today, soon, total: reminders.length };
}

/** Human label for a reminder's timing, e.g. "3 days overdue", "due today". */
export function reminderLabel(reminder: FollowUpReminder): string {
  const n = reminder.daysUntil;
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} overdue`;
  if (n === 0) return 'due today';
  return `due in ${n} day${n === 1 ? '' : 's'}`;
}
